import { Flex, FlexProps } from "styled-system/jsx";
import {
  Button,
  ButtonProps,
  Heading,
  Icon,
  Tag,
  TagProps,
  Text,
} from "@ttc3k/trekker";
import { PropsWithChildren } from "react";
import {
  CloudinaryImage,
  CloudinaryImageProps,
  NavigationBlocker,
} from "shared/components";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "iconoir-react/regular";
import { limitFill } from "@cloudinary/url-gen/actions/resize";
import { useNavigate } from "react-router-dom";
import { css } from "styled-system/css";
import { isLandscape } from "shared/utils/isLandscape";
import { autoGravity } from "@cloudinary/transformation-builder-sdk/qualifiers/gravity";

interface PageSidebarProps extends PropsWithChildren {
  title: string;
  description?: string;
  tagProps?: TagProps;
  imageProps?: CloudinaryImageProps;
  buttonGroupProps?: FlexProps;
  primaryButtonProps?: ButtonProps;
  secondaryButtonProps?: ButtonProps;
  Icon?: React.ForwardRefExoticComponent<
    Omit<React.SVGProps<SVGSVGElement>, "ref"> &
      React.RefAttributes<SVGSVGElement>
  >;
  hideBackButton?: boolean;
}

export const PageSidebar = ({
  Icon: IconElement,
  title,
  description,
  tagProps,
  imageProps,
  buttonGroupProps,
  primaryButtonProps,
  secondaryButtonProps,
  hideBackButton,
  children,
}: PageSidebarProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Flex direction={"column"} w={{ base: "260px" }} gap={"250"}>
      {!hideBackButton && (
        <NavigationBlocker>
          <Button
            visual={"primary"}
            mode={"light"}
            size={"sm"}
            alignSelf={"flex-start"}
            Icon={ArrowLeft}
            onClick={() => navigate(-1)}
          >
            {t("core:BACK")}
          </Button>
        </NavigationBlocker>
      )}

      {IconElement && <Icon size={"xl"} Element={IconElement} />}

      {/* Updated spacing and ordering to match updated Figma designs */}
      <Flex flexDir={"column"} gap="200">
        <Heading size={"h3"}>{title}</Heading>

        {/* The custom list details page has the tags BELOW the heading */}
        {tagProps && <Tag {...tagProps} />}

        {imageProps && (
          <CloudinaryImage
            imageStyleProps={{
              borderRadius: "100",
              objectFit: "cover",
              aspectRatio: "square",
              className: css({ h: "260px" }),
            }}
            resize={
              isLandscape(imageProps.image)
                ? limitFill().height(260).gravity(autoGravity())
                : limitFill().width(260).gravity(autoGravity())
            }
            {...imageProps}
            errorBoundaryProps={{
              mode: "light",
            }}
          />
        )}
        {description && (
          <Text color={"text.mid"} visual={"bodyRegular"}>
            {description}
          </Text>
        )}
      </Flex>

      {/* Buttons are spaced closely now */}
      <Flex flexDir="column" gap="150" {...buttonGroupProps}>
        {primaryButtonProps && <Button size={"lg"} {...primaryButtonProps} />}
        {secondaryButtonProps && (
          <Button size={"lg"} {...secondaryButtonProps} />
        )}
      </Flex>
      {children}
    </Flex>
  );
};
